import { useEffect, useMemo, useRef, useState } from "react";
import { MEXICO_MAP_PATH } from "@/assets/mexico-map-path";
import { projectToMap, type ExploraDestination } from "@/lib/explora-destinations";
import { fitView, layoutLabels, regionPoints, type MapView } from "@/lib/home-map";
import { Button } from "@/components/ui/button";
import { Undo2 } from "lucide-react";
import { HOME_MAP_NEIGHBORS } from "@/assets/home-map-neighbors";
import { HOME_MAP_REGIONS, MAP_ZOOM_DURATION_MS } from "@/lib/home-map-regions";
import { useActiveLang } from "@/hooks/use-active-lang";
import mexicoDay from '@/assets/home/mapa-mexico-realista-dia.jpg.asset.json';

type Props = {
  fullBleed?: boolean;
  destinations: ExploraDestination[];
  counts: Record<string, number>;
  active: string;
  confirmed: Set<string>;
  onSelect: (slug: string) => void;
  label: string;
  toursWord: (n: number) => string;
  allMexico: string;
  hotelLabel: string;
  hotelPoint: { lng: number; lat: number } | null;
  zoomed: boolean;
  region: string | null;
  regionCounts: Record<string, number>;
  onSelectRegion: (id: string) => void;
  onReset: () => void;
};

/** One static SVG map: smooth region zoom, mini map of Mexico, non-overlapping labels. */
export function HomeMexicoMap({ fullBleed=false, destinations, counts, active, confirmed, onSelect, label, toursWord, allMexico, hotelLabel, hotelPoint, zoomed, region, regionCounts, onSelectRegion, onReset }: Props) {
  const lang = useActiveLang();
  const box = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 340, h: 300, left: 0, right: 0 });
  const full = !zoomed;
  useEffect(() => {
    const el = box.current; if (!el) return;
    const ro = new ResizeObserver(([e]) => {
      const w = Math.round(e.contentRect.width);
      const desktop = fullBleed && window.matchMedia('(min-width:1024px) and (orientation:landscape), (min-width:1280px)').matches;
      const hero = el.closest('.home-first-screen');
      const left = desktop ? hero?.querySelector('.home-intro-column')?.getBoundingClientRect() : null;
      const right = desktop ? hero?.querySelector('.home-map-results')?.getBoundingClientRect() : null;
      const bounds = el.getBoundingClientRect();
      setSize({ w, h: desktop ? Math.round(e.contentRect.height) : Math.round(Math.min(Math.max(w * 0.82, 260), 460)), left: left ? left.right - bounds.left + 12 : 0, right: right ? bounds.right - right.left + 12 : 0 });
    });
    ro.observe(el); return () => ro.disconnect();
  }, [fullBleed]);
  const points = useMemo(() => destinations.map(d => ({ d, p: projectToMap(d.lng, d.lat) })), [destinations]);
  const activeP = points.find(x => x.d.citySlug === active)?.p;
  const target = useMemo<MapView>(() => {
    if (full || !activeP) {
      if (size.left || size.right) {
        const fitted = fitView([{ x: -20, y: -35 }, { x: 1040, y: 610 }], size.w - size.left - size.right, size.h, 0.04);
        return { ...fitted, tx: fitted.tx + size.left };
      }
      return fitView([{ x: -20, y: -35 }, { x: 1040, y: 610 }], size.w, size.h, 0.04);
    }
    const selectedRegion = HOME_MAP_REGIONS.find(r => r.id === region);
    if (selectedRegion) return fitView(points.filter(x => selectedRegion.states.some(state => state === x.d.state)).map(x => x.p), size.w, size.h, 0.18, 110);
    return fitView(regionPoints(activeP, points.map(x => x.p)), size.w, size.h, 0.16);
  }, [full, activeP, points, size, region,fullBleed]);

  // Smooth zoom with rAF; instant under reduced motion.
  const [view, setView] = useState<MapView>(target);
  const viewRef = useRef(view);
  useEffect(() => {
    const from = viewRef.current;
    const reduce = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) { viewRef.current = target; setView(target); return; }
    let raf = 0; const t0 = performance.now();
    const step = (t: number) => {
      const k = Math.min(1, (t - t0) / MAP_ZOOM_DURATION_MS); const e = 1 - (1 - k) ** 3;
      const v = { s: from.s + (target.s - from.s) * e, tx: from.tx + (target.tx - from.tx) * e, ty: from.ty + (target.ty - from.ty) * e };
      viewRef.current = v; setView(v);
      if (k < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target]);

  const screen = (p: { x: number; y: number }) => ({ x: p.x * view.s + view.tx, y: p.y * view.s + view.ty });
  const visible = points.map(x => ({ ...x, s: screen(x.p) })).filter(x => x.s.x > -10 && x.s.x < size.w + 10 && x.s.y > -10 && x.s.y < size.h + 10);
  const hotel = hotelPoint ? screen(projectToMap(hotelPoint.lng, hotelPoint.lat)) : null;
  const activeText = (slug: string, name: string) => `${name} · ${toursWord(counts[slug] ?? 0)}`;
  const regions = HOME_MAP_REGIONS.filter(r => (regionCounts[r.id] ?? 0) > 0).map(r => ({ ...r, s: screen(projectToMap(r.lng, r.lat)), text: `${r.names[lang]} · ${toursWord(regionCounts[r.id])}` }));
  const stateAnchors = [
    { name: "Baja California Sur", lng: -111.6, lat: 25.6 }, { name: "Jalisco", lng: -103.7, lat: 20.5 },
    { name: "Guanajuato", lng: -101.1, lat: 21 }, { name: "Ciudad de México", lng: -99.1, lat: 19.4 },
    { name: "Estado de México", lng: -99.7, lat: 19.5 }, { name: "Oaxaca", lng: -96.5, lat: 17.1 },
    { name: "Chiapas", lng: -92.6, lat: 16.5 }, { name: "Campeche", lng: -90.3, lat: 18.8 },
    { name: "Yucatán", lng: -89.2, lat: 20.6 }, { name: "Quintana Roo", lng: -88.1, lat: 19.5 },
  ].filter(s => destinations.some(d => d.state === s.name)).map(s => ({ ...s, s: screen(projectToMap(s.lng, s.lat)) }));
  const items = [
    ...(full ? regions.map(r => ({ id: `region:${r.id}`, x: r.s.x, y: r.s.y, w: r.text.length * 5.5 + 12, h: 22 })) : []),
    ...visible.filter(x => !full && !region && x.d.citySlug === active).map(x => ({ id: x.d.citySlug, x: x.s.x, y: x.s.y, w: activeText(x.d.citySlug, x.d.name).length * 7.6 + 18, h: 24 })),
    ...(hotel ? [{ id: "__hotel", x: hotel.x, y: hotel.y, w: hotelLabel.length * 7 + 4 }] : []),
    ...stateAnchors.map(s => ({ id: `state:${s.name}`, x: s.s.x, y: s.s.y, w: s.name.length * 5, h: 12 })),
    ...visible.filter(x => !full && (region || x.d.citySlug !== active)).sort((a, b) => (counts[b.d.citySlug] ?? 0) - (counts[a.d.citySlug] ?? 0)).map(x => ({ id: x.d.citySlug, x: x.s.x, y: x.s.y, w: x.d.name.length * 7 + 4 })),
  ];
  const labels = layoutLabels(items.map(item => ({ ...item, x: item.x - size.left })), size.w - size.left - size.right, size.h)
    .map(label => ({ ...label, x: label.x + size.left, box: [label.box[0] + size.left, label.box[1], label.box[2] + size.left, label.box[3]] as [number, number, number, number] }));
  const byId = new Map(points.map(x => [x.d.citySlug, x.d]));
  // Mini-map frame: inverse of the current view, in map units.
  const frame = { x: -view.tx / view.s, y: -view.ty / view.s, w: size.w / view.s, h: size.h / view.s };

  const seas = {
    es: ["Golfo de México", "Mar Caribe", "Océano Pacífico"], en: ["Gulf of Mexico", "Caribbean Sea", "Pacific Ocean"],
    fr: ["Golfe du Mexique", "Mer des Caraïbes", "Océan Pacifique"], it: ["Golfo del Messico", "Mar dei Caraibi", "Oceano Pacifico"],
    de: ["Golf von Mexiko", "Karibisches Meer", "Pazifischer Ozean"], pt: ["Golfo do México", "Mar do Caribe", "Oceano Pacífico"],
  }[lang];
  return <div ref={box} data-map-zoomed={zoomed} className={`relative min-w-0 overflow-hidden rounded-xl border border-gold/25 bg-hero ${fullBleed?'hero-map-canvas':''}`} aria-label={label}>
    <svg viewBox={`0 0 ${size.w} ${size.h}`} width={size.w} height={size.h} className="block h-auto w-full" role="group" aria-label={label}>
      <g transform={`translate(${view.tx} ${view.ty}) scale(${view.s})`}>
        <image href={mexicoDay.url} x="-668" y="-170" width="2284" height="910" preserveAspectRatio="none" />
        {HOME_MAP_NEIGHBORS.map(n => <path key={n.name} d={n.d} className="fill-map-context/10 stroke-map-context/20" strokeWidth="0.8" vectorEffect="non-scaling-stroke" />)}
        <path d={MEXICO_MAP_PATH} className="fill-transparent stroke-gold" strokeWidth="1.4" vectorEffect="non-scaling-stroke" />
      </g>
      {[[-93, 27], [-84.5, 17.5], [-110, 17.5]].map(([lng, lat], i) => { const p = screen(projectToMap(lng, lat)); return <text key={i} x={Math.min(size.w - 44, Math.max(44, p.x))} y={p.y} textAnchor="middle" className="pointer-events-none fill-map-context/70 text-[9px] italic">{seas[i]}</text>; })}
      {visible.map(({ d, s }) => {
        const on = !full && (region ? HOME_MAP_REGIONS.find(r => r.id === region)?.states.some(state => state === d.state) : d.citySlug === active);
        return <g key={d.citySlug} role="button" tabIndex={0} aria-pressed={on} aria-label={d.name} onClick={() => onSelect(d.citySlug)} onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelect(d.citySlug); } }} className="cursor-pointer focus:outline-none focus:stroke-ring">
          {(on||d.citySlug===active) && <circle cx={s.x} cy={s.y} r="16" className="fill-primary/25" />}
          <circle cx={s.x} cy={s.y} r={on ? 8 : full ? 3.5 : 6} className={on ? "fill-none stroke-primary" : counts[d.citySlug] > 0 ? "fill-primary/85" : "fill-gold/40"} strokeWidth={on ? 3 : 0} />
          {on && <circle cx={s.x} cy={s.y} r="4" className="fill-hero-foreground" />}
          <circle cx={s.x} cy={s.y} r={full ? 5 : 14} className="fill-transparent" />
        </g>;
      })}
      {hotel && <g aria-label={hotelLabel}>{activeP&&<path d={`M${hotel.x},${hotel.y} L${screen(activeP).x},${screen(activeP).y}`} className="fill-none stroke-primary" strokeDasharray="3 5" strokeWidth="2"/>}<circle cx={hotel.x} cy={hotel.y} r="7" className="fill-hero-foreground" /><circle cx={hotel.x} cy={hotel.y} r="3" className="fill-hero" /></g>}
      {labels.map(l => {
        if (l.id.startsWith("region:")) {
          const r = regions.find(r => `region:${r.id}` === l.id); if (!r) return null;
          const [x0, y0, x1, y1] = l.box;
          return <g key={l.id} role="button" tabIndex={0} aria-label={r.text} onClick={() => onSelectRegion(r.id)} onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onSelectRegion(r.id); } }} className="cursor-pointer focus:outline-none focus:stroke-ring"><rect x={x0} y={y0} width={x1 - x0} height={y1 - y0} rx="4" className="fill-hero stroke-gold/50" /><text x={(x0 + x1) / 2} y={(y0 + y1) / 2 + 3.5} textAnchor="middle" className="fill-gold-light text-[10px] font-semibold">{r.text}</text></g>;
        }
        if (l.id.startsWith("state:")) return <text key={l.id} x={l.x} y={l.y} textAnchor={l.anchor} className="pointer-events-none fill-map-context/75 text-[10px]">{l.id.slice(6)}</text>;
        if (l.id === "__hotel") return <text key={l.id} x={l.x} y={l.y} textAnchor={l.anchor} className="fill-hero-foreground text-[12px] font-medium">{hotelLabel}</text>;
        const d = byId.get(l.id);
        if (!d) return null;
        if (!full && !region && l.id === active) {
          const [x0, y0, x1, y1] = l.box;
          return <g key={l.id}><rect x={x0} y={y0} width={x1 - x0} height={y1 - y0} rx={(y1 - y0) / 2} className="fill-primary" /><text x={(x0 + x1) / 2} y={(y0 + y1) / 2 + 4.5} textAnchor="middle" className="fill-primary-foreground text-[13px] font-bold">{activeText(d.citySlug, d.name)}</text></g>;
        }
        return <text key={l.id} x={l.x} y={l.y} textAnchor={l.anchor} className="pointer-events-none fill-hero-foreground/90 text-[12px]">{d.name}</text>;
      })}
    </svg>
    {!full && <svg viewBox="0 0 1000 580" className="pointer-events-none absolute left-2 top-2 w-[24%] max-w-28 rounded-md border border-gold/30 bg-hero/90" aria-hidden>
      <path d={MEXICO_MAP_PATH} className="fill-hero-map stroke-gold" strokeWidth="5" />
      {!full && <rect x={frame.x} y={frame.y} width={frame.w} height={frame.h} className="fill-none stroke-primary" strokeWidth="10" />}
    </svg>}
    {!full && <Button variant="ghost" type="button" onClick={onReset} className="absolute right-2 top-2 h-auto rounded-full border border-gold/40 bg-hero/90 px-3 py-1.5 text-xs font-medium text-hero-foreground hover:bg-hero-map hover:text-hero-foreground"><Undo2 className="size-3.5" />{allMexico}</Button>}
  </div>;
}
